<template>
  <v-modal id="template-day-item-create" class-name="modal-fullscreen-sm-down" size="lg" @closed="emit('closed')">
    <modal-header class="justify-content-end pt-0" />

    <modal-body class="pt-0">
      <div class="item-types-container w-100 row gx-8 gy-12">
        <div v-for="type in itemTypes" :key="type.key" class="item-type-container col-4">
          <a
            class="text-decoration-none d-flex flex-column align-items-center text-center p-8 rounded-xs hover-bg-light transition-all"
            href="#"
            @click.prevent="addItem(type.key)"
          >
            <div class="item-type-square service-tile-bg rounded-xs mb-8 shadow-sm">
              <v-icon :icon="type.icon" class="text-xl service-icon" :style="{ color: getServiceIconColor(type.key) }" />
            </div>

            <div class="item-type-info fw-bold text-dark text-xs lh-1 px-4" style="font-size: 10px;">{{ type.label }}</div>
          </a>
        </div>
      </div>
    </modal-body>
  </v-modal>
</template>

<script lang="ts" setup>
import { computed, getCurrentInstance, ref } from "vue";
import { TYPE, useToast } from "vue-toastification";
import VModal from "@/components/vendor/basic/modal/VModal.vue";
import ModalHeader from "@/components/vendor/basic/modal/ModalHeader.vue";
import ModalBody from "@/components/vendor/basic/modal/ModalBody.vue";
import useConcepts from "@/helpers/Concepts";
import { mapConcepts } from "@/helpers/ConceptHelper";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import api from "@/api";
import { useI18n } from "vue-i18n";
import { getServiceIconColor } from "@/helpers/ServiceIconColor";

const { t } = useI18n();
const toast = useToast();
const instance = getCurrentInstance();
const $toast = instance?.appContext.config.globalProperties.$toast;

const emit = defineEmits(["closed", "emitReloadTour", "openDetails"]);

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  templateId: {
    type: Number,
    required: true,
  },
  tourDayId: {
    type: Number,
    required: true,
  },
});

const concepts = useConcepts();
const frequentlyUsedTypeOrder = [
  "hotel",
  "transfer",
  "transport",
  "stage_information",
  "restaurant",
  "bus",
  "plane",
  "train",
];

const itemTypes = computed(() => {
  const types = concepts.concepts.value["supplier.type"]
    ? mapConcepts(concepts.concepts.value["supplier.type"])
    : [];
  const priority = new Map(frequentlyUsedTypeOrder.map((type, index) => [type, index]));

  return types
    .map((type, originalIndex) => ({ type, originalIndex }))
    .sort((a, b) => {
      const aPriority = priority.get(a.type.key) ?? Number.MAX_SAFE_INTEGER;
      const bPriority = priority.get(b.type.key) ?? Number.MAX_SAFE_INTEGER;
      return aPriority - bPriority || a.originalIndex - b.originalIndex;
    })
    .map(({ type }) => type);
});

const disableUnassignAgencyUserBtn = ref(false);

const addItem = async (type: string) => {
  const loadingToast = $toast.loading(t("general.button.adding"), "toast-add-template-day-item");

  try {
    const response = await api.activityService.createDayItem(props.agency, props.templateId, props.tourDayId, { type });

    emit("closed");
    emit("openDetails", {
      id: response.id,
      tourDayId: response.activityDayId,
    });
    emit("emitReloadTour");

    loadingToast.update("toast-add-template-day-item", {
      content: t("tourBuilder.dayItemCreated"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    loadingToast.update("toast-add-template-day-item", {
      content: t("tourBuilder.dayITemCreateError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });

    console.error(e.response?.data);
    toast.error(e.response?.data?.message, {
      position: "bottom-right",
      timeout: 5000,
    });
  }
};
</script>

<style lang="scss" scoped>
.item-types-container {
  .service-tile-bg {
    background-color: #f8f9fa;
  }

  .item-type-square {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0.5rem;
    width: 3rem;
    height: 3.5rem;
  }

  .hover-bg-light:hover {
    background-color: var(--uq-light);
    transform: translateY(-2px);
  }

  .transition-all {
    transition: all 0.2s ease-in-out;
  }
}

.service-icon {
  transition: color 0.2s ease;
}
</style>
